/* eslint-disable @next/next/no-img-element */
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { getImage } from '@utils/imageHelper';
import H3 from '@base/H3/H3';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import { useRouter } from 'next/router';
import { useSelectorToStore } from '@hooks/reduxHooks';
import pushRoute from '@utils/pushRoute';
import { breakpoints } from '@styles/index';
import { segment } from '@actions/analytics';

const Banner = styled(Paper)`
  grid-column: span 18;
  background-image: ${({ $bg }) => `url(${$bg})`};
  background-repeat: no-repeat;
  background-position: right bottom;
  background-size: 400px 380px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    background-image: none;
    padding: 0;
    overflow: hidden;
  }
`;

const BannerContent = styled.div`
  display: inline-block;
  width: 336px;
  margin-bottom: 40px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100%;
    padding: 24px 24px 0;
    margin-bottom: 0;
  }
`;

const BannerTitle = styled(H3)`
  font-weight: 600;
  line-height: 30px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
    line-height: 150%;
    margin-bottom: 16px;
  }
`;

const BannerText = styled(P2)`
  margin-left: 12px;
`;

const BannerBlock = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: 8px;
  }
`;

const BannerImage = styled.img`
  display: none;
  user-select: none;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    display: block;
  }
`;

const ExploreButton = styled(Button)`
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin: 24px 0 24px 24px;
    width: 113px;
    font-size: 10px;
    min-height: 32px;
    height: 32px;
  }
`;

const bannerContent = [
  'Boost your performance with adaptive practice tests',
  'Practice every concept in the syllabus',
  'Compare your speed and accuracy with your peers',
  'Download the app and practice on the go'
];

function FreePracticeBanner({ goal, topicName }) {
  const router = useRouter();
  const { goalUID, conceptUID, topicUID } = router.query;
  const goalDetails = useSelectorToStore(
    ({ topologies }) => topologies.data[goalUID]
  );

  const currentGoal = goal || goalDetails;

  const handleClick = () => {
    segment.track('Trial Practice - Explore Goal Clicked', {
      last_primary_source_section: conceptUID ? 'Concept Page' : 'Topic Page',
      topic_uid: conceptUID || topicUID || goalUID,
      topic_title: topicName || 'Practice Problems',
      num_questions_viewed: 'NA'
    });
    pushRoute(
      '/goal/[goalSlug]/[goalUID]',
      currentGoal.relativeLink,
      {},
      'Practice Problems'
    );
  };
  return (
    <Banner $bg={getImage('practice-session/topics/practice-phone.svg')}>
      <BannerImage
        src={getImage('practice-session/topics/practice-banner.svg')}
        width="100%"
        height="222"
      />
      <BannerContent>
        <BannerTitle color="var(--color-text-primary)">
          {`Get unlimited practice with ${currentGoal?.name} subscription`}
        </BannerTitle>
        {bannerContent.map((_content, index) => (
          <BannerBlock key={String(index)}>
            <img
              src={getImage('practice-session/topics/tick.svg')}
              alt="pick"
              width="20"
              height="20"
            />
            <BannerText color="var(--color-text-primary)">
              {_content}
            </BannerText>
          </BannerBlock>
        ))}
      </BannerContent>
      <ExploreButton label="Learn more" onClick={handleClick} />
    </Banner>
  );
}

FreePracticeBanner.propTypes = {
  topicName: PropTypes.string
};

FreePracticeBanner.defaultProps = {
  topicName: ''
};

export default FreePracticeBanner;
